import React, { useState } from 'react'
import DpiBadge from './DpiBadge'
import {
  calculateDpi, getDpiQuality, getOversizedArtworkWarningCopy,
  isArtworkOversized, recommendedPrintDimensions,
} from './artworkQuality'

function displayDimension(value) {
  const number = Number(value)
  return Number.isFinite(number) && number > 0 ? String(Number(number.toFixed(4))) : ''
}

function DimensionInput({ value, disabled, onCommit, onFocus, ...props }) {
  // Keep the user's unfinished text separate from Fabric's calculated dimensions.
  // An untouched field must never round the actual artwork on focus/blur.
  const [draft, setDraft] = useState(null)
  const commit = () => {
    if (draft === null) return
    const number = Number(draft)
    setDraft(null)
    if (!disabled && Number.isFinite(number) && number > 0 && number !== Number(value)) {
      onCommit(number)
    }
  }

  return (
    <input {...props} type="number" min="0.1" step="0.1" disabled={disabled}
      value={draft ?? displayDimension(value)}
      onFocus={event => {
        event.currentTarget.select()
        onFocus?.(event)
      }}
      onChange={event => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={event => {
        if (event.key === 'Enter') {
          event.preventDefault()
          event.currentTarget.blur()
        } else if (event.key === 'Escape') {
          event.preventDefault()
          event.stopPropagation()
          setDraft(null)
        }
      }} />
  )
}

export default function ArtworkSizeControls({ id, image, width, height, sheetSize, disabled, onChange }) {
  const [locked, setLocked] = useState(true)
  const ratio = Number(width) > 0 && Number(height) > 0
    ? Number(height) / Number(width)
    : image.heightPx / image.widthPx
  const oversized = isArtworkOversized(Number(width), Number(height), sheetSize)
  const dpi = calculateDpi(image.widthPx, image.heightPx, Number(width), Number(height))
  const recommended = recommendedPrintDimensions(image.widthPx, image.heightPx)
  const change = (field, value) => {
    const next = Number(value)
    if (!(next > 0)) return
    const dimensions = { printWidthIn: Number(width), printHeightIn: Number(height) }
    dimensions[field] = next
    if (locked && ratio > 0) {
      if (field === 'printWidthIn') dimensions.printHeightIn = Number((next * ratio).toFixed(4))
      else dimensions.printWidthIn = Number((next / ratio).toFixed(4))
    }
    onChange(dimensions)
  }

  return (
    <>
      {oversized ? (
        <div className="artwork-card__sheet-warning" role="alert" data-testid={`artwork-card-sheet-warning-${id}`}>
          {getOversizedArtworkWarningCopy(Number(width), Number(height), sheetSize)}
        </div>
      ) : (
        <div className="artwork-card__print-helper">
          <DpiBadge widthPx={image.widthPx} heightPx={image.heightPx} printWidthIn={Number(width)} printHeightIn={Number(height)} />
          {image.widthPx > 0 && image.heightPx > 0 && (
            <span>{image.widthPx}×{image.heightPx}px — prints ~{(image.widthPx / 300).toFixed(1)}×{(image.heightPx / 300).toFixed(1)}&quot; at 300 DPI</span>
          )}
          {image.dpi > 0 && image.dpi !== 300 && (
            <span>Native DPI: {image.dpi} — prints ~{(image.widthPx / image.dpi).toFixed(1)}×{(image.heightPx / image.dpi).toFixed(1)}&quot; at native resolution</span>
          )}
          {image.widthPx > 0 && image.heightPx > 0 && (image.widthPx < 600 || image.heightPx < 600) && (
            <span className="artwork-card__print-helper-warn">Small image — may look pixelated at larger print sizes</span>
          )}
          {recommended && ['caution', 'poor'].includes(getDpiQuality(dpi)) && (
            <button type="button" disabled={disabled} className="artwork-quality__recommended-action"
              onClick={() => onChange({ printWidthIn: recommended.widthIn, printHeightIn: recommended.heightIn })}>
              Use 300 DPI size ({recommended.widthIn}&quot; × {recommended.heightIn}&quot;)
            </button>
          )}
        </div>
      )}
      <div className="artwork-card__controls artwork-card__dimensions">
        {[['Width', 'printWidthIn', width, sheetSize?.width_in], ['Height', 'printHeightIn', height, sheetSize?.height_in]].map(([label, field, value, max]) => (
          <label key={field}>
            <span>{label}</span>
            <DimensionInput key={id} max={max} value={value} disabled={disabled}
              aria-label={`${image.originalFilename} ${label.toLowerCase()}`}
              data-testid={`artwork-card-${label.toLowerCase()}-${id}`}
              aria-describedby={`artwork-size-entry-help-${id}`}
              aria-invalid={max && Number(value) > max ? 'true' : undefined}
              onCommit={value => change(field, value)} />
          </label>
        ))}
      </div>
      <p className="graphics-panel__helper" id={`artwork-size-entry-help-${id}`}>Press Enter or leave the field to apply the size.</p>
      <div className="artwork-card__aspect-control">
        <button type="button" data-testid={`artwork-card-aspect-${id}`} aria-pressed={locked}
          aria-label={`${locked ? 'Unlock' : 'Lock'} width and height for ${image.originalFilename}`}
          aria-describedby={`artwork-card-aspect-help-${id}`} onClick={() => setLocked(value => !value)}>
          Keep artwork shape: {locked ? 'On' : 'Off'}
        </button>
        <p id={`artwork-card-aspect-help-${id}`}>
          {locked ? 'Recommended: changing width or height adjusts the other automatically.' : 'Width and height change separately and may stretch the artwork.'}
        </p>
      </div>
    </>
  )
}
